import { Card, Col, Collapse, Form, Row } from "react-bootstrap";
import FormInput from "../../components/forms/FormInput";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useGetReferralQuery,
  useGetUserProfileQuery,
  usePatchReferralSettingsMutation,
} from "../../features/users/usersApi";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import EditTemplate from "./components/EditTemplate";
import { RichEditorType } from "../settings/components/RichEditorType";
import { HiChevronDown, HiChevronUp, HiOutlineLockClosed, HiOutlineMail, HiOutlinePaperAirplane, HiOutlineInformationCircle } from "react-icons/hi";
import { HiOutlineArrowPath, HiOutlineClock } from "react-icons/hi2";
import { isMailboxConnected } from "../../utils/signatureHelpers";
import { Link } from "react-router";

interface ReferralData {
  enableReferral: boolean;
  new?: string;
  newSubject?: string;
  newBody?: string;
  followup?: string;
  followupSubject?: string;
  followupBody?: string;
}

enum ReferralType {
  new = "new",
  followup = "followup",
}

interface EmailerType {
  type?: ReferralType;
  subject: string;
  body: string;
}


type SenderMode = "mailbox" | "system";
type FallbackMode = "auto" | "wait" | "stop";

const StepBadge = ({ step }: { step: number }) => (
  <span
    className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary text-white"
    style={{ width: 24, height: 24, minWidth: 24, fontSize: 13, fontWeight: 600 }}
  >
    {step}
  </span>
);

const requiredWhenEnabled = (schema: yup.StringSchema) =>
  schema.when("enableReferral", {
    is: true,
    then: (s) => s.required("Field is required"),
    otherwise: (s) => s.optional(),
  });

const referralSchema = yup
  .object({
    enableReferral: yup.boolean().required(),
    new: requiredWhenEnabled(yup.string()),
    newSubject: requiredWhenEnabled(yup.string()),
    newBody: requiredWhenEnabled(yup.string()),
    followup: requiredWhenEnabled(yup.string()),
    followupSubject: requiredWhenEnabled(yup.string()),
    followupBody: requiredWhenEnabled(yup.string()),
  })
  .required();

const Referral = () => {
  const [show, setShow] = useState(false);
  const [showSenderOptions, setShowSenderOptions] = useState(false);
  const [showFallbackOptions, setShowFallbackOptions] = useState(false);
  const [templateData, setTemplateData] = useState<EmailerType>({
    type: ReferralType.new,
    subject: "",
    body: "",
  });

  const { data: referralData, isLoading } = useGetReferralQuery(undefined);
  const { data: userProfile } = useGetUserProfileQuery(undefined);

  console.log("profile", userProfile);

  const [patchReferralSettings] = usePatchReferralSettingsMutation();

  const methods = useForm({
    resolver: yupResolver(referralSchema),
    defaultValues: {
      enableReferral: false,
      new: "7",
      newSubject: "Do you have a referral for [sender_name] ([sender_company])",
      newBody: `<p><span>We hope you're enjoying the Homeaversary gift, delivered with help from our friends at Memorique!</span></p><p><span style=""> We’re so grateful to have you as part of the [sender_company] family. Your support and referrals mean everything to us and help us continue doing what we love, helping people achieve their real estate goals.</span></p><p><span style=""> If you know someone looking to buy or sell a home in the Area, we’re always here and ready to help. We'll treat your friends and family with the same care we give to you.</span></p><p><span style=""> Wishing you all the best and hope to catch up again soon.</span></p><p><span style=""> Happy Homeaversary!</span></p>`,
      followup: "0",
      followupSubject: "Quick Favor",
      followupBody: `<p><span style="">Just wanted to reach out and say hi! It's been a few months since we last connected.</span></p><p><span style="">If you know anyone a friend, colleague, or family member thinking about buying or selling a home in the Area, [sender_name] would love to help. I've cc'ed them here so you can easily connect.</span></p><p><span style="">Thanks again for all your support!</span></p><p><span style="">Best,</span></p>`,
    },
  });

  const {
    register,
    watch,
    formState: { errors },
  } = methods;

  const enableReferral = watch("enableReferral");
  const followup = watch("followup");
  const newSubject = watch("newSubject");
  const newBody = watch("newBody");
  const followupSubject = watch("followupSubject");
  const followupBody = watch("followupBody");

  const mailboxConnected = isMailboxConnected(
    userProfile?.data?.mailbox_type,
    userProfile?.data?.scope
  );
  const connectedEmail = userProfile?.data?.email;

  const [senderMode, setSenderMode] = useState<SenderMode>("system");
  const [fallbackMode, setFallbackMode] = useState<FallbackMode>("auto");

  useEffect(() => {
    const savedSender = referralData?.data?.referral_sender?.sender;
    if (savedSender) {
      setSenderMode(savedSender);
    } else {
      setSenderMode(mailboxConnected ? "mailbox" : "system");
    }
  }, [referralData, mailboxConnected]);

  useEffect(() => {
    const savedOnTokenExpired = referralData?.data?.referral_sender?.on_token_expired;
    const fallbackFromServer: Record<string, FallbackMode> = {
      system: "auto",
      wait: "wait",
      stop: "stop",
    };
    if (savedOnTokenExpired && fallbackFromServer[savedOnTokenExpired]) {
      setFallbackMode(fallbackFromServer[savedOnTokenExpired]);
    }
  }, [referralData]);

  const changeSender = async (sender: SenderMode) => {
    if (sender === senderMode) return;
    setSenderMode(sender);
    try {
      await patchReferralSettings({ referral_sender: { sender } }).unwrap();
      toast.success("Referral settings updated successfully");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const changeFallbackMode = async (mode: FallbackMode) => {
    if (mode === fallbackMode) return;
    setFallbackMode(mode);
    const onTokenExpired: Record<FallbackMode, "system" | "wait" | "stop"> = {
      auto: "system",
      wait: "wait",
      stop: "stop",
    };
    try {
      await patchReferralSettings({
        referral_sender: { on_token_expired: onTokenExpired[mode] },
      }).unwrap();
      toast.success("Referral settings updated successfully");
    } catch {
      toast.error("Something went wrong");
    }
  };


  const saveReferralSettings = async (data: ReferralData) => {
    try {
      await patchReferralSettings({
        enabled: data.enableReferral,
        first_emailer_days: data.new !== undefined ? Number(data.new) : undefined,
        first_emailer_subject: data.newSubject,
        first_emailer_email_template_body: data.newBody,
        second_emailer_days: data.followup !== undefined ? Number(data.followup) : undefined,
        second_emailer_subject: data.followupSubject,
        second_emailer_email_template_body: data.followupBody,
      }).unwrap();
      toast.success("Referral settings updated successfully");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const handleNewDaysChange = async (value: string) => {
    methods.setValue("new", value);
    await saveReferralSettings(methods.getValues());
  };

  const handleFollowupDaysChange = async (value: string) => {
    methods.setValue("followup", value);
    await saveReferralSettings(methods.getValues());
  };

  const toggleReferral = async (enabled: boolean) => {
    try {
      await patchReferralSettings({ enabled }).unwrap();
      toast.success("Referral settings updated successfully");
    } catch {
      toast.error("Something went wrong");
    }
  };

  const handleAddTemplete = async (data: any) => {
    try {
      const localReferralData = referralData?.data;
      const changedData =
        templateData.type === ReferralType.new
          ? {
            first_emailer_subject: data.subject,
            first_emailer_email_template_body: data.body,
          }
          : {
            second_emailer_subject: data.subject,
            second_emailer_email_template_body: data.body,
          };

      await patchReferralSettings({
        enabled: localReferralData?.enabled,
        first_emailer_days: localReferralData?.first_emailer_days,
        first_emailer_subject: localReferralData?.first_emailer_subject,
        first_emailer_email_template_body:
          localReferralData?.first_emailer_email_template_body,
        second_emailer_days: localReferralData?.second_emailer_days,
        second_emailer_subject: localReferralData?.second_emailer_subject,
        second_emailer_email_template_body:
          localReferralData?.second_emailer_email_template_body,
        ...changedData,
      }).unwrap();
      setShow(false);
      setTemplateData({
        type: ReferralType.new,
        subject: "",
        body: "",
      });

      toast.success("Referral settings updated successfully");
    } catch {
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    if (referralData?.data) {
      const data = referralData?.data;
      methods.reset({
        enableReferral: data?.enabled ?? false,
        new: data.first_emailer_days.toString(),
        newBody: data.first_emailer_email_template_body,
        newSubject: data.first_emailer_subject,
        followup: data.second_emailer_days.toString() ?? "0",
        followupSubject: data.second_emailer_subject,
        followupBody: data.second_emailer_email_template_body,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralData]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Row>
      <Col>
        <Card style={{ border: "none", borderLeft: "1px solid rgba(25, 34, 46, 0.08)", borderRadius: "0", backgroundColor: "transparent" }}>
          <Card.Body style={{ padding: 0, backgroundColor: "transparent" }}>
            <Row className="m-2 mt-3">
              <Col className="col-12">
                <EditTemplate
                  show={show}
                  onHide={() => setShow(false)}
                  data={templateData}
                  onSubmit={handleAddTemplete}
                  editorType={RichEditorType.REFERRAL}
                />
              </Col>

              <Col className="col-12 d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h4 className="mb-1">Referral Emails</h4>
                  <p className="text-muted mb-0">
                    Send 2 automated emails to request referrals after a gift is
                    delivered.
                  </p>
                </div>

              </Col>

              <Col className="col-12">
                <FormProvider {...methods}>
                  <form noValidate>
                    <Card className="mb-3">
                      <Card.Body>
                        <Controller
                          name={`enableReferral`}
                          control={methods.control}
                          render={({ field }) => (
                            <div className="d-flex gap-3 align-items-start">
                              <Form.Check
                                type="switch"
                                id={field.name}
                                checked={field.value}
                                onChange={(e) => {
                                  field.onChange(e.target.checked);
                                  toggleReferral(e.target.checked);
                                  e.target.blur();
                                }}
                              />
                              <div>
                                <div className="d-flex align-items-center gap-2">
                                  <strong>Enable Referral Emails</strong>
                                  {enableReferral && (
                                    <span className="badge bg-success-subtle text-success">
                                      Active
                                    </span>
                                  )}
                                </div>
                                <p className="text-muted mb-0">
                                  {enableReferral
                                    ? "Emails will be sent automatically after a gift is delivered."
                                    : "Automatically send 2 follow-up emails after a gift is delivered."}
                                </p>
                              </div>
                            </div>
                          )}
                        />

                        {!enableReferral && (
                          <div className="d-flex align-items-center gap-2 mt-3 p-2 rounded bg-primary-subtle text-primary">
                            <HiOutlineMail />
                            <span>
                              Once enabled, you can customize your email templates
                              and sender settings.
                            </span>
                          </div>
                        )}
                      </Card.Body>
                    </Card>

                    {!enableReferral ? (
                      <Card className="mb-3">
                        <Card.Body className="d-flex align-items-center gap-3 py-4">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle bg-light"
                            style={{ width: 48, height: 48, minWidth: 48 }}
                          >
                            <HiOutlineLockClosed size={22} className="text-muted" />
                          </div>
                          <div>
                            <strong>Referral Emails are disabled</strong>
                            <p className="text-muted mb-0">
                              Turn on the feature above to set up your email
                              templates and sender preferences.
                            </p>
                          </div>
                        </Card.Body>
                      </Card>
                    ) : (
                      <>
                        <Card className="mb-3">
                          <Card.Body>
                            <div
                              className="d-flex align-items-center justify-content-between mb-1 cursor-pointer"
                              role="button"
                              onClick={() => setShowSenderOptions((prev) => !prev)}
                            >
                              <div className="d-flex align-items-center gap-2 grow">
                                <StepBadge step={1} />
                                <div >
                                  <strong>Choose how referral emails are sent</strong>
                                  <p className="text-muted mb-0">
                                    Select the email address that will be used to send
                                    referral emails.
                                  </p>
                                </div>
                              </div>

                              {!showSenderOptions && <span className="badge bg-success-subtle text-success me-3 ">{senderMode === 'mailbox' ? 'Use my connected mailbox' : 'Use Memorique (System)'}</span>}
                              {showSenderOptions ? <HiChevronUp /> : <HiChevronDown />}
                            </div>


                            <Collapse in={showSenderOptions}>
                              <div className="mt-3">
                                <Row className="g-3">
                                  <Col md={6}>
                                    <div
                                      role="button"
                                      onClick={() =>
                                        mailboxConnected && changeSender("mailbox")
                                      }
                                      aria-disabled={!mailboxConnected}
                                      className={`d-flex align-items-start gap-2 p-3 border rounded ${mailboxConnected ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                                        } ${senderMode === "mailbox" ? "border-primary" : "border-light"
                                        }`}
                                    >
                                      <Form.Check
                                        type="radio"
                                        name="mailboxOption"
                                        checked={senderMode === "mailbox"}
                                        disabled={!mailboxConnected}
                                        readOnly
                                      />
                                      <div>
                                        <div className="d-flex align-items-center gap-2">
                                          <HiOutlineMail />
                                          <strong>Use my connected mailbox</strong>
                                        </div>
                                        <p className="text-muted mb-1">
                                          Emails will be sent from your connected
                                          mailbox.
                                        </p>
                                        {mailboxConnected ? (
                                          <span className="d-flex align-items-center gap-2">
                                            <span className="badge bg-success-subtle text-success">
                                              Connected
                                            </span>
                                            <span className="text-muted">
                                              {connectedEmail}
                                            </span>
                                          </span>
                                        ) : (
                                          <span className="badge bg-light text-muted">
                                            Not connected
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                  <Col md={6}>
                                    <div
                                      role="button"
                                      onClick={() => changeSender("system")}
                                      className={`d-flex align-items-start gap-2 p-3 border rounded ${senderMode === "system" ? "border-primary" : "border-light"
                                        }`}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <Form.Check
                                        type="radio"
                                        name="mailboxOption"
                                        checked={senderMode === "system"}
                                        readOnly
                                      />
                                      <div>
                                        <div className="d-flex align-items-center gap-2">
                                          <HiOutlinePaperAirplane />
                                          <strong>Use Memorique (System)</strong>
                                        </div>
                                        <p className="text-muted mb-1">
                                          Emails will be sent from our system email.
                                        </p>
                                        <span className="text-muted">
                                          hello@memorique.com
                                        </span>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </Collapse>
                          </Card.Body>
                        </Card>

                        {senderMode === "mailbox" && (
                          <Card className="mb-3">
                            <Card.Body>
                              {userProfile?.data?.is_mailbox_token_expired && (
                                <p className="text-danger d-flex align-items-center gap-1" style={{ marginLeft: "36px" }}>
                                  <HiOutlineInformationCircle />
                                  <b>Action required:</b> Your mailbox connection has expired. Please reconnect it from your <Link to="/profile" className="text-decoration-underline">Profile page</Link>.
                                </p>
                              )}
                              <div
                                className="d-flex align-items-center justify-content-between mb-1 cursor-pointer"
                                role="button"
                                onClick={() => setShowFallbackOptions((prev) => !prev)}
                              >
                                <div className="d-flex align-items-center gap-2 grow">
                                  <StepBadge step={2} />
                                  <div >
                                    <strong>If mailbox connection expires</strong>
                                    <p className="text-muted mb-0">
                                      Choose what should happen when your mailbox token
                                      expires or becomes invalid.
                                    </p>
                                  </div>
                                </div>

                                {!showFallbackOptions && <span className="badge bg-success-subtle text-success me-3 ">{fallbackMode === 'auto' ? 'Automatically switch to system' : 'Wait until reconnected'}</span>}
                                {showFallbackOptions ? <HiChevronUp /> : <HiChevronDown />}
                              </div>

                              <Collapse in={showFallbackOptions}>
                                <div className="mt-3">
                                  <Row className="g-3 mb-3">
                                    <Col md={4}>
                                      <div
                                        role="button"
                                        onClick={() => changeFallbackMode("auto")}
                                        className={`h-100 d-flex flex-column gap-2 p-3 border rounded ${fallbackMode === "auto" ? "border-primary" : "border-light"
                                          }`}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <div className="d-flex align-items-start gap-2">
                                          <Form.Check
                                            type="radio"
                                            name="fallbackOption"
                                            checked={fallbackMode === "auto"}
                                            readOnly
                                          />
                                          <div>
                                            <div className="d-flex align-items-center gap-2">
                                              <HiOutlineArrowPath />
                                              <strong>Automatically switch to system</strong>
                                            </div>
                                            <p className="text-muted mb-1">
                                              We'll automatically start sending emails
                                              from hello@memorique.com.
                                            </p>
                                            <span className="badge bg-success-subtle text-success">
                                              Recommended
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                    <Col md={4}>
                                      <div
                                        role="button"
                                        onClick={() => changeFallbackMode("wait")}
                                        className={`h-100 d-flex flex-column gap-2 p-3 border rounded ${fallbackMode === "wait" ? "border-primary" : "border-light"
                                          }`}
                                        style={{ cursor: "pointer" }}
                                      >
                                        <div className="d-flex align-items-start gap-2">
                                          <Form.Check
                                            type="radio"
                                            name="fallbackOption"
                                            checked={fallbackMode === "wait"}
                                            readOnly
                                          />
                                          <div>
                                            <div className="d-flex align-items-center gap-2">
                                              <HiOutlineClock />
                                              <strong>Wait until reconnected</strong>
                                            </div>
                                            <p className="text-muted mb-1">
                                              We'll pause sending until you reconnect
                                              your mailbox.
                                            </p>
                                            <span className="badge bg-warning-subtle text-warning">
                                              Emails will be delayed
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>
                                </div>
                              </Collapse>
                            </Card.Body>
                          </Card>
                        )}

                        <Card className="mb-3">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <StepBadge step={senderMode === "mailbox" ? 3 : 2} />
                                  <strong>Referral email schedule & templates</strong>
                                </div>
                              </div>
                            </div>

                            <Row className="g-3">
                              <Col md={6}>
                                <Card className="h-100">
                                  <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div>
                                        <div className="d-flex align-items-center gap-2">
                                          <strong>Referral Email 1</strong>
                                        </div>
                                        <p className="text-muted mb-0 mt-1">
                                          First follow-up email sent after gift
                                          delivery.
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setTemplateData({
                                            type: ReferralType.new,
                                            subject: methods.getValues("newSubject") ?? "",
                                            body: methods.getValues("newBody") ?? "",
                                          });
                                          setShow(true);
                                        }}
                                        className="btn btn-primary-outline p-1 px-2 font-semibold text-nowrap"
                                      >
                                        Edit Email 1
                                      </button>
                                    </div>
                                    <FormInput
                                      register={register}
                                      type="select"
                                      name="new"
                                      label="Send first email after"
                                      className="mb-2"
                                      errors={errors}
                                      onChange={(e) => handleNewDaysChange(e.target.value)}
                                    >
                                      <option value="7">1 week after recipient recieves my gift</option>
                                      <option value="30">1 month after recipient recieves my gift</option>
                                      <option value="60">2 month after recipient recieves my gift</option>
                                      <option value="90">3 month after recipient recieves my gift</option>
                                      <option value="180">6 month after recipient recieves my gift</option>
                                    </FormInput>
                                    <div className="d-flex align-items-start gap-2 p-2 border rounded bg-light">
                                      <div>
                                        <div className="mb-1">
                                          <strong>Subject:</strong> {newSubject}
                                        </div>
                                        <div className="text-muted" style={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: newBody ?? '' }} >
                                        </div>
                                      </div>
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={6}>
                                <Card className="h-100">
                                  <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div>
                                        <div className="d-flex align-items-center gap-2">
                                          <strong>Referral Email 2</strong>
                                        </div>
                                        <p className="text-muted mb-0 mt-1">
                                          Second follow-up email for an additional
                                          reminder.
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setTemplateData({
                                            type: ReferralType.followup,
                                            subject:
                                              methods.getValues("followupSubject") ?? "",
                                            body: methods.getValues("followupBody") ?? "",
                                          });
                                          setShow(true);
                                        }}
                                        className="btn btn-primary-outline p-1 px-2 font-semibold text-nowrap"
                                      >
                                        Edit Email 2
                                      </button>
                                    </div>
                                    <FormInput
                                      type="select"
                                      name="followup"
                                      label="How often would you like to follow up?"
                                      register={register}
                                      className="mb-2"
                                      errors={errors}
                                      onChange={(e) => handleFollowupDaysChange(e.target.value)}
                                    >
                                      <option value="0">Don't send the followup email</option>
                                      <option value="7">1 week after first email</option>
                                      <option value="30">1 month after first email</option>
                                      <option value="60">2 month after first email</option>
                                      <option value="90">3 month after first email</option>
                                      <option value="180">6 month after first email</option>
                                    </FormInput>
                                    {followup !== "0" && (
                                      <div className="d-flex align-items-start gap-2 p-2 border rounded bg-light">
                                        <div>
                                          <div className="mb-1">
                                            <strong>Subject:</strong> {followupSubject}
                                          </div>
                                          <div className="text-muted" style={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: followupBody ?? '' }} >
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </>
                    )}
                  </form>
                </FormProvider>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row >
  );
};

export default Referral;